// LA CURVA DEL DÍA — cuántos vehículos hay adentro a cada hora.
//
// Este bloque muestra un dato que hasta hoy no existía: en el Desktop, la
// ocupación era un placeholder con ceros desde siempre. Ningún dueño vio nunca
// la forma de su propio día.
//
// Y la forma dice cosas accionables. En la base real con la que se probó, el
// patrón se repite todos los días: sube fuerte hasta las 11, cae a la mitad a
// las 14, repunta a las 17. Eso es una decisión de precios y de personal que
// nadie podía tomar porque nadie podía ver el gráfico.
//
// Se muestra el PROMEDIO de los días elegidos y no un día suelto: un día puntual
// tiene ruido —una lluvia, un feriado— y lo que sirve para decidir es el patrón.
// El día de hoy se superpone aparte, para poder contestar "¿hoy viene distinto?".
//
// Usa la anatomía común (Bloque.js): el titular dice a qué hora se llena y si
// llega a quedarse sin lugar, y el gráfico va debajo como prueba. Antes el
// encabezado era la categoría —"Cómo se llena tu estacionamiento"— y obligaba a
// deducir la conclusión mirando la curva.

import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { LEYENDA, SIN_LEYENDA } from "../Stats/graficos";
import Bloque from "./Bloque";
import { ORIGEN, UI, ESTADO } from "./theme";
import { hora, entero, fecha } from "./format";

const AZUL = ORIGEN.tickets.color;

/** Promedia la curva de varios días, hora por hora. */
function promediarCurvas(dias) {
  const suma = new Array(24).fill(0);
  let n = 0;
  dias.forEach((d) => {
    const h = d?.occupancy?.hourly;
    if (!Array.isArray(h) || h.length !== 24) return;
    h.forEach((v, i) => { suma[i] += Number(v) || 0; });
    n += 1;
  });
  if (!n) return null;
  return suma.map((v) => Math.round((v / n) * 10) / 10);
}

/** La franja en la que el estacionamiento realmente opera. */
function franjaConActividad(curva) {
  const conAutos = curva.map((v, i) => (v > 0 ? i : -1)).filter((i) => i >= 0);
  if (!conAutos.length) return null;
  return { desde: conAutos[0], hasta: conAutos[conAutos.length - 1] };
}

// ── El titular ───────────────────────────────────────────────────────────
//
// La conclusión, no la categoría. Y cambia según lo que la curva permite
// afirmar: sin capacidad configurada no se puede decir "te quedas sin lugar",
// así que se dice lo que sí se sabe —a qué hora se llena y a qué hora afloja—.
function titularDe(datos, ocupacionPico) {
  const pico = hora(datos.horaPico);
  const valle = hora(datos.horaValle);

  if (ocupacionPico !== null && ocupacionPico >= 90) {
    return `A las ${pico} te quedas prácticamente sin lugar`;
  }
  if (ocupacionPico !== null && ocupacionPico >= 70) {
    return `Tu hora más cargada es a las ${pico}, con el ${ocupacionPico}% de tu capacidad ocupada`;
  }
  if (datos.pico > 0 && datos.valle <= datos.pico * 0.55) {
    return `Tu día se llena a las ${pico} y se vacía a la mitad para las ${valle}`;
  }
  if (ocupacionPico !== null) {
    return `Ni en tu mejor hora pasas del ${ocupacionPico}% de tu capacidad`;
  }
  return `Tu hora más cargada es a las ${pico} y la más floja a las ${valle}`;
}

const CurvaDelDia = ({ cerrados, hoy, capacidad }) => {
  const datos = useMemo(() => {
    const curva = promediarCurvas(cerrados);
    if (!curva) return null;

    const franja = franjaConActividad(curva);
    if (!franja) return null;

    // Se recorta a las horas con actividad. Un estacionamiento que abre de 8 a
    // 20 no necesita ver doce horas planas en cero: sólo achican el gráfico.
    const horas = [];
    for (let h = franja.desde; h <= franja.hasta; h++) horas.push(h);

    const pico = Math.max(...curva);
    const horaPico = curva.indexOf(pico);
    const valle = Math.min(...horas.map((h) => curva[h]));
    const horaValle = horas.find((h) => curva[h] === valle);

    const curvaHoy = hoy?.occupancy?.hourly;
    const horaActual = new Date().getHours();

    // Los días que efectivamente aportaron curva. Se guardan las puntas para
    // declarar el rango real: "30 días" que en realidad son 18 con datos es
    // exactamente lo que esta pantalla no puede hacer.
    const conCurva = cerrados.filter((d) => Array.isArray(d?.occupancy?.hourly) && d?.date);

    return {
      horas,
      promedio: horas.map((h) => curva[h]),
      // De hoy sólo se dibujan las horas que YA pasaron: el resto todavía no
      // ocurrió, y una línea que cae a cero al llegar a la hora actual se lee
      // como "se vació", que es falso.
      hoy: Array.isArray(curvaHoy) && curvaHoy.length === 24
        ? horas.map((h) => (h <= horaActual ? (Number(curvaHoy[h]) || 0) : null))
        : null,
      pico, horaPico, valle, horaValle,
      dias: cerrados.filter((d) => Array.isArray(d?.occupancy?.hourly)).length,
      desde: conCurva.length ? conCurva[0].date : null,
      hasta: conCurva.length ? conCurva[conCurva.length - 1].date : null,
    };
  }, [cerrados, hoy]);

  if (!datos) {
    return (
      <Bloque
        nombre="Cómo se llena"
        id="curva-del-dia"
        titular="Todavía no hay datos de ocupación para dibujar tu día"
        frase="Aparecen con la próxima actualización de TicketControl. Si ya actualizaste, se completan solos: cada jornada que cierra dibuja su curva, y en una semana ya se ve el patrón."
      />
    );
  }

  const series = [
    { name: `Promedio de ${datos.dias} día${datos.dias === 1 ? "" : "s"}`, data: datos.promedio, type: "area" },
  ];
  if (datos.hoy) series.push({ name: "Hoy", data: datos.hoy, type: "line" });

  const options = {
    chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
    // El promedio lleva la identidad —es el patrón, la serie del gráfico— y la
    // línea de hoy va en el neutro oscuro, igual que la tendencia de 7 días de
    // la pestaña Ingresos: no es un origen de plata, es una referencia puesta
    // encima. Estaba en el ámbar de ESTADO, que en esta pantalla significa
    // "esto hay que mirarlo" —los tickets anulados, las señales de Para mirar—;
    // un día normal quedaba pintado con el color de las alertas, y theme.js
    // dice expresamente que ESTADO no va como serie suelta dentro de un
    // gráfico de identidad.
    colors: datos.hoy ? [AZUL, UI.texto] : [AZUL],
    stroke: { curve: "smooth", width: datos.hoy ? [2, 3] : [2], dashArray: datos.hoy ? [0, 0] : [0] },
    fill: {
      type: datos.hoy ? ["gradient", "solid"] : ["gradient"],
      gradient: { shadeIntensity: 0.2, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] },
    },
    // Dos series con nombre → leyenda siempre. Una sola → el título la nombra.
    // La leyenda estaba escrita desde el principio y no se veía: la tapaba una
    // regla global del tema (ver el encabezado de ../Stats/graficos.js).
    legend: datos.hoy ? LEYENDA : SIN_LEYENDA,
    dataLabels: { enabled: false },   // 13 puntos juntos: las etiquetas se pisan
    grid: { borderColor: UI.linea, strokeDashArray: 3, padding: { left: 4, right: 4 } },
    xaxis: {
      categories: datos.horas.map((h) => hora(h)),
      labels: { style: { colors: UI.textoSuave, fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true },
      axisBorder: { show: false }, axisTicks: { show: false },
    },
    yaxis: {
      labels: { style: { colors: UI.textoSuave, fontSize: "11px" }, formatter: (v) => entero(v) },
      title: { text: "Vehículos adentro", style: { color: UI.textoSuave, fontSize: "11px", fontWeight: 500 } },
    },
    // La capacidad como línea, para que "lleno" sea una posición en el gráfico y
    // no un número que hay que recordar.
    annotations: capacidad > 0 ? {
      yaxis: [{
        y: capacidad,
        borderColor: ESTADO.alerta.color,
        strokeDashArray: 5,
        label: {
          text: `Capacidad: ${entero(capacidad)}`,
          style: { color: "#fff", background: ESTADO.alerta.color, fontSize: "11px" },
          position: "left", offsetX: 90,
        },
      }],
    } : {},
    tooltip: {
      shared: true,
      y: { formatter: (v) => (v === null ? "—" : `${entero(v)} vehículos`) },
    },
  };

  const ocupacionPico = capacidad > 0 ? Math.round((datos.pico / capacidad) * 100) : null;

  const rango = datos.desde && datos.hasta
    ? `${fecha(datos.desde)} – ${fecha(datos.hasta)} · ${datos.dias} día${datos.dias === 1 ? "" : "s"} con datos de ocupación`
    : `${datos.dias} día${datos.dias === 1 ? "" : "s"} con datos de ocupación`;

  return (
    <Bloque
      nombre="Cómo se llena"
      id="curva-del-dia"
      titular={titularDe(datos, ocupacionPico)}
      frase={`Es el promedio de vehículos adentro en cada hora${datos.hoy ? ", con el día de hoy encima para comparar" : ""}. Sirve para decidir a qué hora te conviene tener más gente atendiendo y en qué franja tienes lugar de sobra para promocionar.`}
      rango={rango}
      acento={AZUL}
    >
      <ReactApexChart options={options} series={series} type="area" height={280} />

      {/* La lectura del gráfico, escrita. El dueño no tiene por qué deducirla. */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 20, marginTop: 12,
        paddingTop: 12, borderTop: `1px solid ${UI.linea}`,
      }}>
        <Dato
          titulo="Tu hora más cargada"
          valor={hora(datos.horaPico)}
          detalle={`${entero(Math.round(datos.pico))} vehículos${ocupacionPico !== null ? ` · ${ocupacionPico}% de tu capacidad` : ""}`}
        />
        <Dato
          titulo="Tu hora más floja"
          valor={hora(datos.horaValle)}
          detalle={`${entero(Math.round(datos.valle))} vehículos`}
        />
        {ocupacionPico !== null && ocupacionPico < 70 && (
          <Dato
            titulo="Lugar disponible en el pico"
            valor={`${entero(Math.max(0, capacidad - Math.round(datos.pico)))} lugares`}
            detalle="libres incluso en tu momento más cargado"
          />
        )}
      </div>
    </Bloque>
  );
};

const Dato = ({ titulo, valor, detalle }) => (
  <div>
    <div style={{ fontSize: 11, color: UI.textoSuave, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {titulo}
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: UI.texto, lineHeight: 1.2 }}>{valor}</div>
    <div style={{ fontSize: 12, color: UI.textoSuave }}>{detalle}</div>
  </div>
);

export default CurvaDelDia;
